import { useMemo, useState } from 'react';
import type { OfferingV2Response } from '../../types/offeringsV2';
import { formatAed, offeringFromPrice, offeringHasPriceRange } from '../offers/offerUtils';
import { ITEMS_MAX, ineligibleReasonOf, type PackageFieldErrors } from './packageUtils';
import { reasonText, type PackagesT } from './packagesI18n';

export interface PackageServicePickerProps {
  t: PackagesT;
  /** The garage's services (listMyOfferingsV2); null while loading. */
  catalogue: OfferingV2Response[] | null;
  loadError: boolean;
  /** Picked offering ids, in package order. */
  selectedIds: string[];
  /** Name for a picked id the catalogue no longer lists (so it can still be removed). */
  nameOf: (offeringId: string) => string;
  disabled?: boolean;
  fieldErrors: PackageFieldErrors;
  onToggle: (offeringId: string) => void;
}

/**
 * Spec 081b — searchable list of the garage's services. Eligible = ACTIVE and not blocked; the rest
 * stay listed, greyed, with the reason. Picked services show as removable chips (2–6).
 */
export default function PackageServicePicker(props: PackageServicePickerProps) {
  const { t, catalogue, loadError, selectedIds, nameOf, disabled, fieldErrors, onToggle } = props;
  const [query, setQuery] = useState('');
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const full = selectedIds.length >= ITEMS_MAX;

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (catalogue ?? []).filter((o) => !q || o.name.toLowerCase().includes(q));
  }, [catalogue, query]);

  return (
    <div className="pkg-picker">
      {selectedIds.length > 0 && (
        <ul className="pkg-chips" aria-label={t('packages.picker.selected', { n: selectedIds.length })}>
          {selectedIds.map((id) => (
            <li key={id} className="pkg-chip">
              <span className="pkg-chip__name">{nameOf(id)}</span>
              <button type="button" className="pkg-chip__remove" disabled={disabled}
                aria-label={t('packages.picker.remove', { name: nameOf(id) })} onClick={() => onToggle(id)}>×</button>
            </li>
          ))}
        </ul>
      )}
      <div className="pkg-picker__bar">
        <input type="search" className="pkg-picker__search" placeholder={t('packages.picker.search')}
          aria-label={t('packages.picker.search')} value={query} onChange={(e) => setQuery(e.target.value)} />
        <span className="offer-muted" data-testid="pkg-selected-count">{t('packages.picker.selected', { n: selectedIds.length })}</span>
      </div>
      <p className="offer-field-hint">{full ? t('packages.picker.max') : t('packages.picker.hint')}</p>

      {catalogue == null && !loadError && <p className="offer-muted" role="status">{t('packages.picker.loading')}</p>}
      {loadError && <div className="error-message" role="alert">{t('packages.picker.loadFailed')}</div>}
      {catalogue != null && catalogue.length === 0 && <p className="offer-muted">{t('packages.picker.empty')}</p>}
      {catalogue != null && catalogue.length > 0 && rows.length === 0 && <p className="offer-muted">{t('packages.picker.noMatch')}</p>}

      {rows.length > 0 && (
        <ul className="offer-service-list pkg-picker__list" aria-invalid={fieldErrors.items ? true : undefined}
          aria-describedby={fieldErrors.items ? 'pkg-err-items' : undefined}>
          {rows.map((o) => {
            const reason = ineligibleReasonOf(o);
            const checked = selected.has(o.id);
            // A picked service that became ineligible stays uncheckable so the partner can remove it.
            const blocked = !checked && (!!reason || full);
            const price = offeringFromPrice(o);
            return (
              <li key={o.id} data-testid={`pkg-service-${o.id}`}
                className={`offer-service${checked ? ' offer-service--checked' : ''}${reason ? ' pkg-service--ineligible' : ''}`}>
                <label>
                  <input type="checkbox" checked={checked} disabled={disabled || blocked} onChange={() => onToggle(o.id)}
                    aria-describedby={reason ? `pkg-reason-${o.id}` : undefined} />
                  <span className="offer-service__name">{o.name}</span>
                  {o.isCarSpecific && <span className="pkg-badge pkg-badge--muted">{t('packages.fixer.carSpecific')}</span>}
                </label>
                <span className="offer-service__price" dir="ltr">
                  {price == null ? '—' : offeringHasPriceRange(o) ? `${t('packages.picker.from')} ${formatAed(price)}` : formatAed(price)}
                </span>
                {reason && (
                  <div className="pkg-service__reason" id={`pkg-reason-${o.id}`}>
                    {t('packages.picker.unavailable', { reason: reasonText(t, reason) })}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {fieldErrors.items && <div className="offer-field-error" id="pkg-err-items" role="alert">{fieldErrors.items}</div>}
    </div>
  );
}
